import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import { authStorage, decodeTokenClaims } from '../services/authStorage';
import {
  requestAuthToken,
  submitApplicationOnboarding,
  exchangeOnboardingHandoff,
  logoutFromCore,
  refreshAuthToken,
  getProfile,
  CoreApiError,
} from '../services/coreApi';
import { deleteAccount as requestAccountDeletion } from '../services/backendApi';
import { coreConfig } from '../config/core';
import {
  AuthState,
  LoginCredentials,
  RegisterData,
  AccountType,
  LoginOutcome,
  RegisterOutcome,
  OnboardingHandoffOutcome,
  CoreUser,
  ExchangeOnboardingHandoffResponse,
} from '../types';
import { openRoleHome, SESSION_HOME_EVENT } from '../utils/sessionNavigation';
import { passportRolesFromClaims, rolesFromClaims } from '../utils/userDisplay';

const INVALID_CREDENTIALS = 'Invalid email or password.';
const ACCESS_DENIED = 'Your account does not have access to Passport. Contact an administrator.';

interface AuthContextValue extends AuthState {
  accountType: AccountType;
  availableRoles: AccountType[];
  switchRole: (role: AccountType) => void;
  login: (credentials: LoginCredentials) => Promise<LoginOutcome>;
  register: (data: RegisterData) => Promise<RegisterOutcome>;
  completeOnboardingHandoff: (params: {
    code: string;
    state: string;
  }) => Promise<OnboardingHandoffOutcome>;
  logout: () => void;
  deleteAccount: () => Promise<void>;
  clearError: () => void;
}

function hasPassportAccess(accessToken: string): boolean {
  const claims = decodeTokenClaims(accessToken);
  const roles = Array.isArray(claims.roles)
    ? claims.roles.filter((role): role is string => typeof role === 'string')
    : [];
  const hasPassportRole = roles.some(role =>
    ['employee', 'manager', 'verifier'].includes(role.toLowerCase()),
  );
  return hasPassportRole && claims.applicationId === coreConfig.applicationId;
}

/**
 * Hosted signup stores the JWT from account creation. Person onboarding grants
 * the Passport role afterwards, so that token still has an empty roles claim.
 * Refresh rebuilds the claims from Core.
 */
async function sessionWithPassportAccess(session: {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
}): Promise<{ accessToken: string; refreshToken?: string; expiresIn: number } | null> {
  if (hasPassportAccess(session.accessToken)) {
    return {
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      expiresIn: session.expiresIn ?? 3600,
    };
  }

  if (!session.refreshToken) return null;

  try {
    const refreshed = await refreshAuthToken(session.refreshToken);
    if (!hasPassportAccess(refreshed.accessToken)) {
      void logoutFromCore(refreshed.refreshToken);
      return null;
    }
    return {
      accessToken: refreshed.accessToken,
      refreshToken: refreshed.refreshToken,
      expiresIn: refreshed.expiresIn,
    };
  } catch {
    void logoutFromCore(session.refreshToken);
    return null;
  }
}

function userFromHandoff(response: ExchangeOnboardingHandoffResponse): CoreUser {
  if (response.user) {
    return {
      id: response.user.id,
      email: response.user.email,
      fullName: response.user.fullName,
      phone: response.user.mobile ?? undefined,
    };
  }
  return {
    id: response.userId ?? '',
    email: '',
  };
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    loading: true,
    error: null,
  });
  const coreProfileFetched = useRef(false);

  useEffect(() => {
    const user = authStorage.getCachedUser();
    const token = authStorage.getToken();
    setState({
      user,
      isAuthenticated: !!(user && token),
      loading: false,
      error: null,
    });
  }, []);

  useEffect(() => {
    if (!state.isAuthenticated || coreProfileFetched.current) return;
    if (!authStorage.getToken()) return;
    coreProfileFetched.current = true;

    void getProfile()
      .then(coreUser => {
        setState(prev => {
          const merged = { ...(prev.user ?? {}), ...coreUser };
          authStorage.updateCachedUser(merged);
          return { ...prev, user: merged };
        });
      })
      .catch(() => {
        // Keep the cached token user if Core profile is unavailable
      });
  }, [state.isAuthenticated]);

  const login = useCallback(async (credentials: LoginCredentials): Promise<LoginOutcome> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const data = await requestAuthToken({
        applicationId: coreConfig.applicationId,
        email: credentials.email.trim(),
        password: credentials.password,
      });

      if (!hasPassportAccess(data.accessToken)) {
        if (data.refreshToken) void logoutFromCore(data.refreshToken);
        setState(prev => ({ ...prev, loading: false, error: ACCESS_DENIED }));
        return { outcome: 'error', error: ACCESS_DENIED };
      }

      authStorage.setSession({
        accessToken: data.accessToken,
        expiresIn: data.expiresIn,
        refreshToken: data.refreshToken,
        user: data.user,
      });

      setState({ user: data.user, isAuthenticated: true, loading: false, error: null });
      return { outcome: 'authenticated' };
    } catch (err) {
      if (err instanceof CoreApiError) {
        if (err.status === 403) {
          const error =
            err.message && !/^(forbidden|request failed)$/i.test(err.message)
              ? err.message
              : ACCESS_DENIED;
          setState(prev => ({ ...prev, loading: false, error }));
          return { outcome: 'error', error };
        }

        const error =
          err.status === 401
            ? INVALID_CREDENTIALS
            : err.status >= 500
              ? 'Something went wrong. Please try again.'
              : err.message;

        setState(prev => ({ ...prev, loading: false, error }));
        return { outcome: 'error', error };
      }

      const error = 'Something went wrong. Please try again.';
      setState(prev => ({ ...prev, loading: false, error }));
      return { outcome: 'error', error };
    }
  }, []);

  const register = useCallback(async (data: RegisterData): Promise<RegisterOutcome> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await submitApplicationOnboarding({
        email: data.email.trim(),
        password: data.password,
        applicationId: coreConfig.applicationId,
        roleName: coreConfig.defaultRoleName,
        firstName: data.firstName.trim(),
        mobile: data.mobile?.trim() || undefined,
      });

      if (response.accessToken && response.user) {
        authStorage.setSession({
          accessToken: response.accessToken,
          expiresIn: response.expiresIn ?? 3600,
          refreshToken: response.refreshToken,
          user: response.user,
        });

        setState({ user: response.user, isAuthenticated: true, loading: false, error: null });
        return { outcome: 'authenticated' };
      }

      setState(prev => ({ ...prev, loading: false, error: null }));
      return { outcome: 'pending', message: response.message ?? '' };
    } catch (err) {
      if (err instanceof CoreApiError) {
        let error: string;
        if (err.status === 401) {
          error = INVALID_CREDENTIALS;
        } else if (err.status === 409) {
          error = 'You already have access. Try signing in instead.';
        } else if (err.status >= 500) {
          error = 'Something went wrong. Please try again.';
        } else {
          error = err.message;
        }

        setState(prev => ({ ...prev, loading: false, error }));
        return { outcome: 'error', error };
      }

      const error = 'Something went wrong. Please try again.';
      setState(prev => ({ ...prev, loading: false, error }));
      return { outcome: 'error', error };
    }
  }, []);

  const completeOnboardingHandoff = useCallback(
    async (params: { code: string; state: string }): Promise<OnboardingHandoffOutcome> => {
      setState(prev => ({ ...prev, error: null }));

      try {
        const response = await exchangeOnboardingHandoff({
          code: params.code,
          applicationId: coreConfig.applicationId,
          state: params.state,
        });

        if (!response.accessToken) {
          return {
            outcome: 'pending',
            message: 'Your account has been created and is waiting for approval.',
          };
        }

        const session = await sessionWithPassportAccess({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          expiresIn: response.expiresIn,
        });
        if (!session) {
          setState(prev => ({ ...prev, error: ACCESS_DENIED }));
          return { outcome: 'error', error: ACCESS_DENIED };
        }

        const user = userFromHandoff(response);
        authStorage.setSession({
          accessToken: session.accessToken,
          expiresIn: session.expiresIn,
          refreshToken: session.refreshToken,
          user,
        });

        setState({ user, isAuthenticated: true, loading: false, error: null });
        return { outcome: 'authenticated' };
      } catch (err) {
        const error =
          err instanceof CoreApiError
            ? err.status === 401
              ? 'Onboarding could not be completed. Please try again.'
              : err.status >= 500
                ? 'Something went wrong. Please try again.'
                : err.message
            : 'Something went wrong. Please try again.';

        setState(prev => ({ ...prev, error }));
        return { outcome: 'error', error };
      }
    },
    [],
  );

  const logout = useCallback(() => {
    const refreshToken = authStorage.getRefreshToken();
    coreProfileFetched.current = false;
    authStorage.clearSession();
    setState({ user: null, isAuthenticated: false, loading: false, error: null });
    if (refreshToken) {
      logoutFromCore(refreshToken);
    }
  }, []);

  const deleteAccount = useCallback(async () => {
    const refreshToken = authStorage.getRefreshToken();
    await requestAccountDeletion();
    coreProfileFetched.current = false;
    authStorage.clearSession();
    setState({ user: null, isAuthenticated: false, loading: false, error: null });
    if (refreshToken) {
      void logoutFromCore(refreshToken);
    }
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const [, setRoleTick] = useState(0);
  useEffect(() => {
    const syncRole = () => setRoleTick(tick => tick + 1);
    window.addEventListener(SESSION_HOME_EVENT, syncRole);
    return () => window.removeEventListener(SESSION_HOME_EVENT, syncRole);
  }, []);
  const availableRoles = passportRolesFromClaims(rolesFromClaims(authStorage.getTokenClaims()));
  const accountType: AccountType = authStorage.getActiveRole() ?? 'employee';

  const switchRole = useCallback((role: AccountType) => {
    const available = passportRolesFromClaims(rolesFromClaims(authStorage.getTokenClaims()));
    if (!available.includes(role) || authStorage.getActiveRole() === role) return;
    authStorage.setActiveRole(role);
    setRoleTick(tick => tick + 1);
    openRoleHome(role);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        accountType,
        availableRoles,
        switchRole,
        login,
        register,
        completeOnboardingHandoff,
        logout,
        deleteAccount,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuthContext = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
};
